import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  isUuid,
  markTrainingLeft,
  onboardingStepSearch,
  saveTrainingResult,
  useOnboardingStepViewed,
} from '@features/Onboarding';
import { MobileSession, Session, type FirstCardNote, type TrainingResult } from '@features/Session';
import { trackEvent } from '@shared/analytics';
import { paths } from '@shared/config';
import { useIsSidebarMobile } from '@widgets/Sidebar';

/**
 * Первая тренировка знакомства — обычная сцена тренировки по уже созданной сессии модуля
 * (её запускает экран 1). Перезагрузка продолжает ту же сессию. Конец тренировки и «выйти»
 * ведут на итог знакомства; экран итогов тренировки (Completion) здесь не показывается.
 */
export default function OnboardingTrainingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const isMobile = useIsSidebarMobile();
  const moduleId = new URLSearchParams(search).get('module') ?? '';
  useOnboardingStepViewed('training');
  // Ушёл с тренировки в этой вкладке («Назад» тоже): экран знакомства не перекинет обратно (B5).
  useEffect(() => markTrainingLeft, []);

  const toResult = useCallback(
    (result: TrainingResult | null) =>
      navigate(
        { pathname: paths.onboardingResult, search: onboardingStepSearch(search) },
        { replace: true, state: { result } },
      ),
    [navigate, search],
  );

  const onExit = useCallback(() => {
    trackEvent('onboarding_skipped', { step: 'training' });
    toResult(null);
  }, [toResult]);

  const onComplete = useCallback(
    (result: TrainingResult) => {
      saveTrainingResult(result);
      trackEvent('first_training_completed', {
        correct: result.correct,
        total: result.total,
        answered: result.answered,
      });
      toResult(result);
    },
    [toResult],
  );

  // Слова новые: до первого ответа говорим, что не знать — нормально (решение владельца 2026-10-19).
  const firstCardNote = useMemo<FirstCardNote>(
    () => ({ mcq: t('onboarding.training.noteMcq'), input: t('onboarding.training.noteInput') }),
    [t],
  );

  if (!isUuid(moduleId)) {
    return <Navigate to={{ pathname: paths.onboarding, search: onboardingStepSearch(search) }} replace />;
  }

  const Screen = isMobile ? MobileSession : Session;
  return (
    <Screen
      key={moduleId}
      cram
      moduleId={moduleId}
      onExit={onExit}
      onComplete={onComplete}
      firstCardNote={firstCardNote}
      firstExercise="mcq"
    />
  );
}
